import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import html2canvas from 'html2canvas';

/**
 * ScreenCaptureService
 * --------------------
 * Grabs (a) a JPEG screenshot of the current Angular viewport and
 * (b) a small structured snapshot of what's on screen (route, page
 * title, headings, breadcrumb, visible record IDs).
 *
 * The AI uses the screenshot to literally see what the user is looking
 * at, and the structured context to drill into the source code / DB
 * by ID rather than by name-matching.
 *
 * Triggered by user phrases like "analyze my screen", "read my screen",
 * "what am I looking at" — detection lives in AiChatStateService.
 *
 * Pages NOT to capture: the AI assistant widget itself (would be a
 * recursive image of the chat over the chat). We strip <app-ai-assistant>
 * via html2canvas `ignoreElements`.
 */

export interface ScreenContext {
  /** Angular router URL at capture time, e.g. "/admin/customer/abc-123". */
  route: string;
  /** Best-effort page name pulled from h1 / .page-title / document.title. */
  pageTitle: string;
  /** Visible breadcrumb text, if any breadcrumb element exists. */
  breadcrumb?: string;
  /** Headings visible on screen (h1/h2/h3). Useful for AI to ground its answer. */
  visibleHeadings: string[];
  /**
   * Best-effort entity IDs visible on screen. Pulled from:
   *   - route params (UUID/ObjectId-shaped segments)
   *   - elements with [data-record-id] + [data-record-type] attributes
   * AI uses these as MongoDB lookup keys when the user asks it to "dig in".
   */
  visibleEntities: Array<{ type: string; value: string }>;
  /** ISO timestamp of capture. */
  timestamp: string;
}

export interface ScreenCapture {
  /** Raw base64 (no `data:image/jpeg;base64,` prefix). */
  imageBase64: string;
  mediaType: 'image/jpeg';
  context: ScreenContext;
  /** Approximate base64 length in bytes — for diagnostics / size guarding. */
  approxBytes: number;
}

@Injectable({ providedIn: 'root' })
export class ScreenCaptureService {

  constructor(private router: Router) {}

  /**
   * Capture a JPEG of the current viewport + structured context.
   * Resolves even if html2canvas errors — returns context-only in that case
   * (image will be empty string).
   */
  async capture(): Promise<ScreenCapture> {
    const context = this.extractContext();
    let imageBase64 = '';

    try {
      // Cap pixel ratio so a 1920×1080 viewport stays around 250–500 KB JPEG.
      // scale 1.0 with quality 0.7 hits a sweet spot for Bedrock vision.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

      const canvas = await html2canvas(document.body, {
        scale: 1.0 / dpr,           // cancel out device pixel ratio scaling
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        // Skip the AI assistant widget — capturing it would just show the chat
        // overlay over the chat page, which is useless context.
        ignoreElements: (el: Element) => {
          const tag = (el.tagName || '').toLowerCase();
          if (tag === 'app-ai-assistant') return true;
          if (el.classList && el.classList.contains('ai-skip-capture')) return true;
          return false;
        }
      });

      // Downscale to satisfy BOTH width and height limits:
      // - Width cap (1600 px) → keeps base64 size manageable for Bedrock.
      // - Height cap (7500 px) → Bedrock rejects any image dimension > 8000 px
      //   (real error: "messages.0.content.0.image.source.base64.data: At
      //   least one of the image dimensions exceed max allowed size: 8000
      //   pixels"). Tall pages like list views easily blow past this.
      const MAX_W = 1600;
      const MAX_H = 7500;
      let outCanvas: HTMLCanvasElement = canvas;
      const wRatio = canvas.width  > MAX_W ? MAX_W / canvas.width  : 1;
      const hRatio = canvas.height > MAX_H ? MAX_H / canvas.height : 1;
      const ratio = Math.min(wRatio, hRatio);
      if (ratio < 1) {
        const scaled = document.createElement('canvas');
        scaled.width  = Math.floor(canvas.width  * ratio);
        scaled.height = Math.floor(canvas.height * ratio);
        const ctx = scaled.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
          outCanvas = scaled;
        }
      }

      const dataUrl = outCanvas.toDataURL('image/jpeg', 0.7);
      imageBase64 = dataUrl.split(',')[1] || '';
    } catch (e) {
      // Vision degrades gracefully — AI still gets structured context.
      // eslint-disable-next-line no-console
      console.warn('[ScreenCapture] html2canvas failed; sending context-only.', e);
    }

    return {
      imageBase64,
      mediaType: 'image/jpeg',
      context,
      approxBytes: Math.floor(imageBase64.length * 0.75)
    };
  }

  /**
   * Pull route + visible-DOM context. Runs synchronously — fast enough
   * to call on every screen-aware message without UI jank.
   */
  private extractContext(): ScreenContext {
    const route = this.router.url || window.location.pathname;

    // Page title: first <h1>, then known title class names, then <title>.
    const titleEl = document.querySelector('h1, .page-title, .panel-title, .header-title');
    const pageTitle = ((titleEl?.textContent || document.title || '').trim()).slice(0, 120);

    // Up to 12 visible headings — gives AI grounding to describe sections.
    const headings = Array.from(document.querySelectorAll('h1, h2, h3, .panel-heading, .section-title'))
      .map(el => (el.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(t => t.length > 0 && t.length < 140)
      .slice(0, 12);

    // Entities: pull UUID/ObjectId-shaped segments from the route + any
    // [data-record-id] markers components opt into.
    const entities: Array<{ type: string; value: string }> = [];

    // Route-based IDs: matches /entity/<id> where id looks like a uuid or 24-hex.
    const routeIdRegex = /\/([a-z][a-z0-9_\-]*)\/([a-f0-9]{8,}-?[a-f0-9\-]{0,}|\d{6,})/gi;
    let m: RegExpExecArray | null;
    while ((m = routeIdRegex.exec(route)) !== null) {
      const type = m[1];
      const value = m[2];
      // Skip generic path segments
      if (['admin', 'edit', 'view', 'list', 'new'].includes(type.toLowerCase())) continue;
      entities.push({ type, value });
      if (entities.length > 6) break;
    }

    // DOM-based opt-in IDs: components can mark <div data-record-id="..." data-record-type="customer">
    document.querySelectorAll('[data-record-id]').forEach(el => {
      const id = el.getAttribute('data-record-id');
      const type = el.getAttribute('data-record-type') || 'record';
      if (id && entities.length < 12) entities.push({ type, value: id });
    });

    // Breadcrumb text, if present
    const crumbEl = document.querySelector('.breadcrumb, .breadcrumbs, nav[aria-label="breadcrumb"]');
    const breadcrumb = crumbEl
      ? (crumbEl.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 240)
      : undefined;

    return {
      route,
      pageTitle,
      breadcrumb,
      visibleHeadings: headings,
      visibleEntities: entities,
      timestamp: new Date().toISOString()
    };
  }
}
